import React, { useEffect, useMemo, useRef, useState } from 'react';
import { bringToLifeMarkFrom } from 'components/video-gen/wizardEntry';
import AdEditorNavBar from 'components/ad-builder/ad-editor-preview-ad-variant-bar/Navbar';
import AdEditorPreview from 'components/ad-builder/Preview';
import AdEditorToolbar, {
  AdEditorButton,
  ToolbarOrientation,
} from 'components/ad-builder/Toolbar';
import Button from 'components/button/Button';
import Centered from 'components/centered/Centered';
import AppPage from 'components/page/app-page/AppPage';
import SaveButton from 'components/save-button/SaveButton';
import {
  DELETE_VARIANTS,
  UPDATE_EDITOR_DATA,
} from 'graphql/ad-variants/mutations';
import { GET_EDITOR_DATA } from 'graphql/vacancies/queries';
import CampaignValuesMessages from 'components/ad-builder/CampaignValuesMessages';
import useCampaignValues from 'hooks/useCampaignValues';
import { ACK_NOTICE } from 'graphql/campaign-values/mutations';
import { useVacancyChannelSelection } from 'hooks/useVacancyChannelSelection';
import ChannelChangeRecovery from 'components/channel-toggle-group/ChannelChangeRecovery';
import useBeforeUnloadWarning from 'hooks/useBeforeUnloadWarning';
import useNavigationContext from 'hooks/context/nav-context';
import { useMutation } from 'hooks/sympl-mutation';
import { useQuery } from 'hooks/sympl-query';
import { useApolloClient } from '@apollo/client';
import { getLocalAppSession } from 'utils/storageHelpers';
import {
  earliestStoredPictureExpiry,
  nextLinkRenewalAt,
} from 'utils/templatePreviewHelpers';

import tw, { styled } from 'twin.macro';
import {
  describeMissingFields,
  toSavableVariant,
} from 'utils/adVariantSaveHelpers';
import { AdEditorSavablePreviewState, AdVariant } from 'types/adEditorTypes';
import {
  AdChannel,
  AdCreativeType,
  AdPlacement,
  VISUAL_AD_CHANNELS,
} from 'types/adTypes';

import VariantGenerator from './VariantGenerator';
import { adDeletionMessage } from './adDeletionMessage';
import MissingFields from 'components/page/app-page/MissingFields';
import useGetStartedContext from 'hooks/context/get-started-context';
import useApiList from 'hooks/useApiList';
import { EXPERIENCE } from 'types/targetingTypes';
import { Routes } from 'types/routeTypes';
import useAdEditorContext from 'hooks/context/ad-editor-context';
import useVideoGenContext from 'hooks/context/video-gen-context';
import {
  getChannelIcon,
  getFormattedChannel,
  isVariantSupported,
} from 'utils/adEditorHelpers';
import ToolTip from 'components/tooltip/ToolTip';
import Sidebar from './Sidebar';
import { ToastTypes } from 'types/notificationTypes';
import { useToastNotifications } from 'hooks/notificationHooks';
import { useNavigate } from 'react-router-dom';
import { uniqueId } from 'lodash';

/**
 * SYM-6214. How the editor waits for pictures the server is still drawing.
 *
 * A save queues one render per ad and per design, so they land a second or two after the ad list
 * does. Rather than ask for renders of its own -- the volume SYM-6213 exists to remove -- the
 * editor re-asks the ad LIST, which is one cheap read that carries every picture that has landed.
 *
 * ⚠️ Bounded on purpose, and the bound is the safety property. An unbounded poll is a storm with a
 * timer on it: if a render genuinely fails, nothing ever fills that field and a poll with no cap
 * would ask forever, from every open editor. After the cap the editor stops and the live path takes
 * over for whatever is still missing, which is exactly the behaviour before this ticket.
 */
const PREPARING_POLL_MS = 3000;
const PREPARING_MAX_ATTEMPTS = 5;

interface AdEditorQueryResponse {
  editorData: {
    adVariants: AdVariant[];
  };
}

interface AdEditorQueryPayload {
  vacancy: number;
}

export interface AdEditorDeleteVariantPayload {
  variantId: number;
  input: { vacancy_id: number };
}

export interface AdEditorUpdatePayload {
  input: { vacancy_id: number; adVariants: AdEditorSavablePreviewState[] };
}

export enum AdEditorSideBarTabs {
  VISUAL_OPTIONS = 'Visuals',
  OVERLAY_OPTIONS = 'Overlays',
  BANNER_OPTIONS = 'Banner Options',
  LOGO_OPTIONS = 'Logo',
  TEXT_OPTIONS = 'Text',
  SHAPE_OPTIONS = 'Shape',
  COLOR_OPTIONS = 'Color',
}

const AdEditor: React.FC = () => {
  const navigate = useNavigate();

  const { nextItem, refetchCheckList } = useGetStartedContext();
  const { addToast } = useToastNotifications();

  const { activeVacancy, brands, currentVacancy } = useNavigationContext();

  const {
    activeTab,
    currentVariant,
    previewChannel,
    setActiveTab,
    setPayload,
    setPreviewChannel,
    initVariants,
    applyStoredPictures,
    variants,
    hasAdTemplatesV2,
  } = useAdEditorContext();

  const { isAnyVideoRendering } = useVideoGenContext();

  const nonDeletedVariants = useMemo(
    () =>
      [...variants]
        .filter(({ isDeleted }) => !isDeleted)
        .sort(
          (a, b) =>
            Object.values(AdPlacement).indexOf(a.placement) -
            Object.values(AdPlacement).indexOf(b.placement)
        ),
    [variants]
  );

  const missingFields = useMemo(
    () => describeMissingFields(nonDeletedVariants),
    [nonDeletedVariants]
  );

  const {
    loading: loadingEditorData,
    data: editorResponse,
    refetch: refetchEditorData,
  } = useQuery<AdEditorQueryResponse, AdEditorQueryPayload>(GET_EDITOR_DATA, {
    skip: !activeVacancy,
    variables: { vacancy: activeVacancy! },
    fetchPolicy: 'network-only',
  });

  const targeting = useMemo(() => {
    return currentVacancy?.targeting;
  }, [currentVacancy]);

  const hasUnsavedChanges = useMemo(
    () => !!variants.find(({ isDirty, isDeleted }) => isDirty || isDeleted),
    [variants]
  );

  // Closing the tab or reloading throws this work away silently (SYM-6037).
  useBeforeUnloadWarning(hasUnsavedChanges);

  useEffect(() => {
    if (editorResponse?.editorData.adVariants) {
      initVariants(
        editorResponse?.editorData.adVariants.map((variant) => ({
          ...variant,
          // SYM-6185. The server's yes/no, tied to the file it is about.
          bring_to_life_resource_id: bringToLifeMarkFrom(variant),
          isDirty: false,
          isDeleted: false,
          company: variant.company ?? currentVacancy?.brand?.name, // TODO: can we remove this one?
          uuid: uniqueId(),
        }))
      );
    }
  }, [editorResponse?.editorData.adVariants]);

  /**
   * SYM-6214. Re-ask the ad list while the server is still drawing, then stop.
   *
   * Only while something is actually missing: an ad that carries a picture, or that never had a
   * design, is not waiting for anything, so a fully prepared campaign polls zero times. The counter
   * is a ref rather than state because changing it must not itself cause a render -- that would
   * re-run this effect and turn a bounded wait into a loop.
   */
  const preparingAttemptsRef = useRef(0);
  const apolloClient = useApolloClient();
  // Bumped after every re-ask, answered or not, so the next attempt is scheduled even when the answer
  // changed nothing on screen (still drawing) or failed -- otherwise the wait stopped after one.
  const [preparingTick, setPreparingTick] = useState(0);

  /*
   * ⚠️ Only while the SERVER says a saved change is being drawn. It was "any ad with a design and
   * no stored picture", which also matched ads nothing will ever draw: ads not saved since this
   * shipped, and a video ad carrying a design (the job skips videos). Bike Maker has one (ad
   * 36681), so every load of that campaign re-asked five times for pictures that could not come
   * -- measured in the QA access log, 16 Sep 2026. Those ads are drawn live instead.
   */
  const waitingForPictures = variants.some(
    (ad) => ad.preview_pending && !ad.isDeleted
  );

  /*
   * ⚠️ A SEPARATE request, not refetchEditorData(), and `no-cache` so its answer never reaches the
   * editor query. That query's answer REBUILDS the editor -- every ad replaced and given a new
   * uuid -- and a re-ask used to go through it: Steven saw the whole campaign blank a few times,
   * three seconds apart, on QA, 16 Sep 2026. A re-ask can only bring pictures, so only the picture
   * fields are copied onto the ads already on screen. If the ads differ (a save added or removed
   * one), nothing is copied: that save's own reload rebuilds. Used by the pending poll below and by
   * the link renewal after it.
   */
  const refetchStoredPictures = (): Promise<void> => {
    if (!activeVacancy) return Promise.resolve();
    return apolloClient
      .query<AdEditorQueryResponse, AdEditorQueryPayload>({
        query: GET_EDITOR_DATA,
        variables: { vacancy: activeVacancy },
        fetchPolicy: 'no-cache',
        // The header hooks/sympl-query adds to every request. Without it this answered 400 on QA
        // (16 Sep 2026, 10:18:26): apolloClient.query() bypasses that hook.
        context: {
          headers: {
            'Active-Customer': getLocalAppSession()?.activeCustomer,
          },
        },
      })
      .then((result) => {
        const fresh = result.data?.editorData.adVariants;
        if (fresh) applyStoredPictures(fresh);
      })
      .catch(() => undefined);
  };

  useEffect(() => {
    if (!waitingForPictures || !activeVacancy) {
      // Nothing pending. Reset so a later save gets its own full allowance of attempts.
      preparingAttemptsRef.current = 0;
      return;
    }

    if (preparingAttemptsRef.current >= PREPARING_MAX_ATTEMPTS) return;

    const timer = setTimeout(() => {
      preparingAttemptsRef.current += 1;

      refetchStoredPictures().finally(() =>
        setPreparingTick((tick) => tick + 1)
      );
    }, PREPARING_POLL_MS);

    return () => clearTimeout(timer);
  }, [waitingForPictures, activeVacancy, preparingTick]);

  /*
   * SYM-6214 (Astra's check D, QA, 17 Sep 2026). The stored picture links live 60 minutes. Left open
   * longer, the editor opened the designs picker with dead links: nine tiles asked for, nine
   * "Request has expired", nothing asked the API for fresh ones. So the links are renewed shortly
   * before they die, through the same picture-only request the pending poll uses, and again when
   * the tab comes back into view past that time -- a laptop asleep over lunch. Each renewal is one
   * request per campaign per hour; the generation guard in applyStoredPictures keeps a renewal from
   * ever overwriting a newer save, and a renewal touches picture fields only, never an edit.
   */
  const linksExpireAt = useMemo(
    () => earliestStoredPictureExpiry(variants),
    [variants]
  );

  /*
   * SYM-6214 (Astra's fourth review). An ad whose drawing FAILED is repaired by the server on its
   * own, 30 minutes later, at most twice. The editor stopped asking when it read `failed`, so an
   * editor left open would never learn the pictures arrived. Now: whenever the tab comes back into
   * view and some ad reads `failed`, the picture fields are re-asked once, through the same
   * generation-guarded request. No timer, no loop: one request per return to the tab, and only
   * while a failed ad is on screen.
   */
  const hasFailedAd = variants.some(
    (ad) => ad.preview_state === 'failed' && !ad.isDeleted
  );
  const lastFailedRefreshRef = useRef(0);
  useEffect(() => {
    if (!hasFailedAd || !activeVacancy) return undefined;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastFailedRefreshRef.current < 60_000) return;
      lastFailedRefreshRef.current = Date.now();
      refetchStoredPictures();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [hasFailedAd, activeVacancy]);
  const lastLinkRefreshRef = useRef(0);
  useEffect(() => {
    if (!linksExpireAt || !activeVacancy) return undefined;

    const renew = () => {
      lastLinkRefreshRef.current = Date.now();
      refetchStoredPictures();
    };
    // Renew shortly before the links die, never more than once a minute, and never in the past;
    // nextLinkRenewalAt is the tested arithmetic. The renewal brings new links, which re-arm this.
    const at = nextLinkRenewalAt(
      linksExpireAt,
      lastLinkRefreshRef.current,
      Date.now()
    );
    const timer = setTimeout(renew, at - Date.now());
    const onVisible = () => {
      if (
        document.visibilityState === 'visible' &&
        nextLinkRenewalAt(
          linksExpireAt,
          lastLinkRefreshRef.current,
          Date.now()
        ) <=
          Date.now() + 1_000
      ) {
        renew();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [linksExpireAt, activeVacancy]);

  const [
    updateVariants,
    { loading: updatingVariants, error: updateVariantsError },
  ] = useMutation<{}, AdEditorUpdatePayload>(UPDATE_EDITOR_DATA);

  const [
    deleteVariants,
    { loading: deletingVariants, error: deletingVariantsError },
  ] = useMutation<{}, AdEditorDeleteVariantPayload>(DELETE_VARIANTS);

  const channelSelection = useVacancyChannelSelection(false);

  const updateAdHandler = async (): Promise<void> => {
    try {
      // Filter only dirty, non-deleted variants
      const dirtyVariants = nonDeletedVariants.filter(({ isDirty }) => isDirty);

      if (dirtyVariants.length) await updateVariantsHandler(dirtyVariants);

      const variantsToDelete = variants
        .filter(({ isDeleted }) => isDeleted)
        .filter(({ id }) => id);

      if (variantsToDelete.length) {
        // SYM-6203. One request per ad is what the server offers, but the
        // customer hears about it once, with the number. See
        // adDeletionMessage.ts.
        const outcomes = await Promise.all(
          variantsToDelete.map(({ id }) => deleteVariantsHandler(id!))
        );
        const deleted = outcomes.filter(Boolean).length;
        const { ok, text } = adDeletionMessage({
          deleted,
          failed: outcomes.length - deleted,
        });

        addToast({
          type: ok ? ToastTypes.SUCCESS : ToastTypes.ERROR,
          description: text,
        });

        if (!ok) return Promise.reject();
      }

      // SYM-6351 (Mark, 28 Sep 2026). Translated texts are now saved: only now does the "still in
      // <old language>" message go. Clearing it when Translate was pressed lost the warning for a
      // customer who left without saving.
      if (translatedUnsaved && activeVacancy) {
        setTranslatedUnsaved(false);
        await ackNotice({
          variables: {
            vacancyId: activeVacancy,
            notice: 'ad-texts-language',
            input: {},
          },
        }).catch(() => undefined);
        await refetchCampaignValues();
      }

      await refetchEditorData();
      await refetchCheckList();
    } catch (_) {}
  };

  const updateVariantsHandler = async (
    dirtyVariants: AdVariant[]
  ): Promise<void> => {
    try {
      /**
       * SYM-5926: assembled by toSavableVariant() rather than inline, so the payload can be asserted.
       * The callout used to be overwritten with the brand default here, below the spread, which threw
       * away whatever the customer had typed while the request still returned 200.
       */
      const savableVariants = dirtyVariants.map((v) =>
        toSavableVariant(v, currentVacancy?.brand?.default_banner_title)
      );

      const { errors } = await updateVariants({
        variables: {
          input: {
            vacancy_id: activeVacancy as number,
            adVariants: savableVariants,
          },
        },
      });

      if (errors) Promise.reject();

      return Promise.resolve();
    } catch (_) {
      addToast({
        type: ToastTypes.ERROR,
        description: 'An error occurred while updating the ad variants',
      });
      return Promise.reject();
    }
  };

  /**
   * Deletes one ad and says whether it went. SYM-6203: it no longer raises a
   * message of its own, because several of these run at once and the customer
   * should be told once, by the caller, with the number.
   */
  const deleteVariantsHandler = async (variantId: number): Promise<boolean> => {
    try {
      const { errors } = await deleteVariants({
        variables: {
          variantId,
          input: {
            vacancy_id: activeVacancy as number,
          },
        },
      });

      return !errors;
    } catch (_) {
      return false;
    }
  };

  const showVariantGenerator = useMemo(
    () => !loadingEditorData && !nonDeletedVariants.length,
    [nonDeletedVariants]
  );

  const { data: languages } = useApiList('languages');

  /*
   * SYM-6351. A change to the campaign's job title or location -- from the Job title row, an
   * update of live ads, or Targeting -- makes the SERVER redraw ads the editor did not touch. Each
   * new answer is a cue to fetch their pictures the picture-only way (never a rebuild, which would
   * throw away unsaved edits); applyStoredPictures takes a newer server redraw for untouched ads.
   */
  const { state: campaignValues, refetch: refetchCampaignValues } =
    useCampaignValues();
  // Translations applied as unsaved changes; cleared when saved (above) or when the editor reloads.
  const [translatedUnsaved, setTranslatedUnsaved] = useState(false);
  const [ackNotice] = useMutation<
    {},
    { vacancyId: number; notice: string; input: {} }
  >(ACK_NOTICE);
  const campaignValuesKey = campaignValues
    ? JSON.stringify([campaignValues.ad_values, campaignValues.ads])
    : null;
  const seenCampaignValuesKey = useRef<string | null>(null);
  useEffect(() => {
    if (!campaignValuesKey) return;
    const first = seenCampaignValuesKey.current === null;
    seenCampaignValuesKey.current = campaignValuesKey;
    if (!first) refetchStoredPictures();
  }, [campaignValuesKey]);

  const campaignLanguage = languages.find(
    ({ key }) => key === currentVacancy?.language_id
  )?.label;

  useEffect(() => {
    // No need to fetch other data if we can't generate payload anyways
    if (!targeting) return;

    (async () => {
      const brandsData = brands;
      const jobCategory = targeting?.sub_job_type_name;

      const language =
        languages.find(({ key }) => key === targeting?.language_id)?.label ??
        'Dutch';

      setPayload({
        jobFunction: targeting?.vac_name ?? '',
        companyName:
          brandsData?.find(({ id }) => id === targeting?.brand_id)?.name ?? '',
        location: targeting?.locations?.[0]?.name ?? 'Antwerp',
        jobCategory: jobCategory ?? '',
        experienceLevel:
          targeting?.experience
            ?.map((code) => EXPERIENCE.find(({ code: c }) => c === code)?.name)
            .join(', ') ?? 'None',
        language,
      });
    })();
  }, [targeting, languages, setPayload]);

  const checkVariantSupported = (channel: AdChannel): boolean =>
    !!currentVariant &&
    isVariantSupported(
      currentVariant.placement,
      channel,
      currentVariant.creative_type ??
        (currentVariant.placement === AdPlacement.REELS ||
          channel === AdChannel.TIKTOK)
        ? AdCreativeType.VIDEO
        : AdCreativeType.IMAGE
    );

  const displaySaveBttn = () => {
    return (
      !showVariantGenerator && (
        <SaveButton
          loading={updatingVariants}
          shouldSave={hasUnsavedChanges}
          disabled={
            updatingVariants ||
            deletingVariants ||
            !hasUnsavedChanges ||
            missingFields.length > 0 ||
            isAnyVideoRendering
          }
          error={updateVariantsError || deletingVariantsError}
          onClick={updateAdHandler}
        />
      )
    );
  };

  const showSidebar = !!activeTab && !showVariantGenerator;

  return (
    <AppPage
      heading="Ad Builder"
      disablePadding={true}
      loading={loadingEditorData || updatingVariants || deletingVariants}
      cta={
        <div tw="flex space-x-5">
          {!!(nonDeletedVariants.length && missingFields.length) && (
            <MissingFields missingFields={missingFields} />
          )}
          {!hasUnsavedChanges && nextItem?.key === 'vacancy_page' ? (
            <Button
              variant="indigo"
              onClick={() => navigate(Routes.JOB_POSTING)}
              testId="adbuilder-next-step-button"
            >
              Go to next step &rarr;
            </Button>
          ) : (
            displaySaveBttn()
          )}
        </div>
      }
      isSideBarOpen={showSidebar}
      setActiveTab={setActiveTab}
      sideBarMenu={[
        AdEditorSideBarTabs.VISUAL_OPTIONS,
        // SYM-6252. For a customer with templates v2 the panel is about templates, so the tab says
        // so. Only the LABEL changes: the tab strip maps a non-enum item by its position, and this
        // one sits where OVERLAY_OPTIONS sits, so the active tab is still tracked as before.
        hasAdTemplatesV2 ? 'Templates' : AdEditorSideBarTabs.OVERLAY_OPTIONS,
      ]}
      activeTabIndex={
        Object.values(AdEditorSideBarTabs).indexOf(
          activeTab || AdEditorSideBarTabs.VISUAL_OPTIONS
        ) ?? 0
      }
      sideBarContent={showSidebar && <Sidebar />}
    >
      <div
        css={[
          tw`flex flex-col h-full lg:flex-row`,
          !showVariantGenerator ? tw`relative` : tw`py-20 bg-gray-50`,
        ]}
      >
        {!showVariantGenerator && (
          <AdEditorNavBar refetchEditorData={refetchEditorData} />
        )}
        <div tw="w-full h-full overflow-y-auto">
          <Centered id="container" tw="h-auto">
            {showVariantGenerator ? (
              <VariantGenerator refetchEditorData={refetchEditorData} />
            ) : (
              <EditorWrapper id="editor-wrapper" activeTab={activeTab}>
                <PreviewContainer>
                  <div>
                    <CampaignValuesMessages
                      campaignLanguage={campaignLanguage}
                      translatedUnsaved={translatedUnsaved}
                      onTranslated={() => setTranslatedUnsaved(true)}
                    />
                    <div tw="mb-4">
                      <AdEditorToolbar
                        orientation={ToolbarOrientation.HORIZONTAL}
                        divide={false}
                        customButtons={VISUAL_AD_CHANNELS.map((channel) => {
                          const isChannelEnabled =
                            currentVacancy?.channels?.includes(channel);
                          const isSupported = checkVariantSupported(channel);
                          const tooltipText = !isSupported
                            ? `${getFormattedChannel(
                                channel
                              )} does not support ${
                                currentVariant?.placement
                              } ads`
                            : isChannelEnabled
                            ? 'Show a preview for ' +
                              getFormattedChannel(channel)
                            : `${getFormattedChannel(
                                channel
                              )} is currently not selected`;

                          return (
                            <ToolTip key={channel} text={tooltipText}>
                              <AdEditorButton
                                isActive={previewChannel === channel}
                                onClick={() => setPreviewChannel(channel)}
                                id={`channel-button-${channel.toLowerCase()}`}
                              >
                                <div tw="[svg]:p-1">
                                  <div
                                    css={[
                                      (!isChannelEnabled || !isSupported) &&
                                        tw`opacity-50 grayscale`,
                                    ]}
                                  >
                                    {getChannelIcon(channel, 32)}
                                  </div>
                                </div>
                              </AdEditorButton>
                            </ToolTip>
                          );
                        })}
                      />
                      <ChannelChangeRecovery
                        recoveries={channelSelection.recoveries}
                        retryChange={channelSelection.retryChange}
                      />
                      {previewChannel &&
                        !currentVacancy?.channels?.includes(previewChannel) &&
                        checkVariantSupported(previewChannel) && (
                          <div tw="mt-2 text-sm text-amber-600 text-center font-medium bg-amber-50 px-3 py-2 rounded-md border border-amber-200 flex items-center justify-center space-x-2">
                            <span>
                              {getFormattedChannel(previewChannel)} is currently
                              not active.
                            </span>
                            <button
                              disabled={channelSelection.disabled}
                              onClick={() =>
                                channelSelection.changeChannel(
                                  previewChannel,
                                  true
                                )
                              }
                              tw="text-amber-700 hover:text-amber-800 underline font-semibold"
                            >
                              Activate
                            </button>
                          </div>
                        )}
                    </div>

                    <AdEditorPreview />
                  </div>
                </PreviewContainer>
              </EditorWrapper>
            )}
          </Centered>
        </div>
      </div>
    </AppPage>
  );
};

const EditorWrapper = styled.div<{ activeTab?: AdEditorSideBarTabs }>`
  ${tw`relative h-full pt-4 pb-16 lg:pb-1 lg:grid`}
  ${({ activeTab }) => activeTab && tw`hidden`}
`;

const PreviewContainer = styled.div(
  tw`relative pb-5 flex flex-col-reverse items-center w-full lg:(block p-0 m-0) lg:w-full`
);

export default AdEditor;
